/*
 *   Copyright (c) 2020 BuserNet Consulting LLC
 *   All rights reserved.
 */
import _forEach from 'lodash/forEach';
import differenceInMinutes from 'date-fns/differenceInMinutes';
import reportError from '../../../services/error-handler/reportError';
import bnd3Service from '../../../services/bnd3.service';
import utilityService from '../../../services/utility.service';
import ChartConst from '../../../constants/chart.constant';
import AppConst from '../../../constants/app.constant';
import bnChartDataService from '../../../services/chartData.service';
import d3 from '../../../globalexports/d3.export';
import {
  roundOfNumber,
} from '../../../services/utils/roundOfNumber';
import reportService from '../../../services/report.service';
import gridViewService from '../../../views/Gridview/Gridview.service';

function xAxisGridService() {
  const content = {};
  content.drawXaxis = (
    sourceData,
    chartObj,
    width,
    x,
    dateFormat,
    dateFormat2,
    settings,
    airportSqaureWidth,
    squareWidth,
    xAxisTopContainerHeight,
    travelAxis,
    majorContainerHeight,
    normalTextHeight,
    zuluTextWidth,
    isTopAxis,
  ) => {
    let reduceTimeTicks = false;
    const zeroZuluLineData = [];
    if (!sourceData) {
      return;
    }
    const data = utilityService.clone(sourceData[0].forecast);
    const sourceForDateStrip = {
      uniqueDays: [],
      sourceData: [],
      zeroLineSource: [],
    };
    _forEach(data, (airport) => {
      if (sourceForDateStrip.uniqueDays.indexOf(dateFormat2(airport.forecast_ts)) < 0) {
        const temp = {
          display: dateFormat2(airport.forecast_ts),
          dateValue: airport.forecast_ts,
        };
        sourceForDateStrip.uniqueDays.push(dateFormat2(airport.forecast_ts));
        sourceForDateStrip.sourceData.push(utilityService.clone(temp));
        const temp2 = utilityService.clone(temp);
        if (settings.timeZone === AppConst.timeZones.local.key) {
          sourceForDateStrip.zeroLineSource.push(temp2.dateValue.setHours(0, 0, 0, 0));
        } else {
          // TBD: Improve this logic. Not sure why with ZULU setting hours to 0000 not working
          const formattedDate = dateFormat(temp2.dateValue);
          if (formattedDate.substring(0, 2) === '00') {
            sourceForDateStrip.zeroLineSource.push(
              utilityService.subtractMinutes(temp2.dateValue, 0),
            );
          }
        }
      }
    });
    data.shift();
    travelAxis.domain(d3.extent(data, (d) => d.forecast_ts));
    _forEach(data, (value) => {
      value.sunMoonPositionData = utilityService.getSunMoonCalculation(
        value.forecast_ts_local,
        value.lat,
        value.lng,
      );
    });
    const daysForDayNight = bnChartDataService.getDaysInData(
      data,
      'forecast_ts',
      'forecast_ts_local',
    );

    if ((width - airportSqaureWidth) - zuluTextWidth * data.length < ChartConst
      .gridTickMinOffset) {
      reduceTimeTicks = true;
    }
    // Setting Domain for X-axis
    x.domain(d3.extent(data, (d) => d.forecast_ts));

    const zeroLineContainer = chartObj.append('g');
    const dayNightIndicator = chartObj.append('g')
      .attr('id', 'day-night-indicator-meteo')
      .attr('class', 'day-night-indicator')
      .attr('transform', () => `${'translate(-'}${airportSqaureWidth},  ${isTopAxis
        ? 0
        : -(normalTextHeight * 2)})`);
    const routeMajorDates = chartObj.append('g')
      .attr('id', 'meteo-major-date-strip')
      .attr('transform', `translate(-${airportSqaureWidth}, 0)`)
      .selectAll('majorDays')
      .data(sourceForDateStrip.sourceData)
      .enter();
    routeMajorDates.append('text')
      .text((d) => d.display)
      .attr('style', 'font-weight: bold')
      .attr('x', (...args) => {
        const d = args[0];
        const key = args[1];
        const lastXOfAxis = width - 0;
        const dayStart = (key === 0
          ? x(d.dateValue) - airportSqaureWidth
          : x(d.dateValue));
        let dayEnd = lastXOfAxis;
        const selfEle = utilityService.getSelfElement(args);
        const widthOfDayText = bnd3Service.getSelectionWidth(d3.select(selfEle));
        if (sourceForDateStrip.sourceData[key + 1]) {
          dayEnd = Math.min(x(sourceForDateStrip.sourceData[key + 1].dateValue), lastXOfAxis);
        }
        dayEnd -= ((key + 1) * 2);
        const availablespace = (dayEnd - dayStart);
        if (availablespace > widthOfDayText) {
          const xValue = dayStart + (availablespace - widthOfDayText) / 2;
          return roundOfNumber(xValue);
        }
        return -200;
      })
      .attr('y', (isTopAxis
        ? -2.5
        : roundOfNumber(normalTextHeight + 2.5)));
    bnd3Service.drawDayNightIndicator(
      xAxisTopContainerHeight,
      dayNightIndicator,
      travelAxis,
      width,
      daysForDayNight,
    );
    // Drawing x-time tick to allign them with the boxes
    // Time ticks are drawn
    const shouldLogOrNot = () => {
      const keyAlreadyLoggedOn = 'gridviewhourlog';
      const gridviewhourlog = localStorage.getItem(keyAlreadyLoggedOn);
      if (!gridviewhourlog) {
        localStorage.setItem(keyAlreadyLoggedOn, new Date());
        return true;
      }
      const gridviewhourlogData = new Date(gridviewhourlog);
      const timeDiff = differenceInMinutes(new Date(), gridviewhourlogData);
      if (timeDiff > 10) {
        localStorage.setItem(keyAlreadyLoggedOn, new Date());
        return true;
      }
      return false;
    };
    const logWrongData = () => {
      if (data && data.length > 2) {
        const [item1, item2, item3] = data;
        const time1 = new Date(item1.forecast_ts);
        const time2 = new Date(item2.forecast_ts);
        const time3 = new Date(item3.forecast_ts);
        const diffInMin1And2 = differenceInMinutes(time2, time1);
        const diffInMin2And3 = differenceInMinutes(time3, time2);
        if (diffInMin1And2 !== diffInMin2And3) {
          const mapData = gridViewService.getDataByKey(settings.departureTime);
          const logData = {
            settings,
            mapData,
            keys: Object.keys(gridViewService.getGridViewDataMap()),
            item1: { forecast_ts: item1.forecast_ts },
            item2: { forecast_ts: item2.forecast_ts },
            item3: { forecast_ts: item3.forecast_ts },
          };
          if (shouldLogOrNot()) {
            reportService.postJsonToBlob().then((serviceDataBlob) => {
              reportService.sendDataToJsonBlob(logData).then((logDataBlob) => {
                reportError({
                  message: 'Gridview Hours Difference Wrong Logger',
                  code: 'Gridview Hours Difference Wrong Logger',
                  serviceDataBlob,
                  logDataBlob,
                  diffInMin1And2,
                  diffInMin2And3,
                });
              });
            });
          }
        }
      }
    };
    setTimeout(() => {
      try {
        logWrongData();
      } catch (ex) {
        console.log(ex);
      }
    }, 3000);
    chartObj.selectAll('x-time-ticks')
      .data(data)
      .enter()
      .append('text')
      .text((d, i) => (reduceTimeTicks
        ? !(i % 2)
          ? dateFormat(d.forecast_ts)
          : ''
        : dateFormat(d.forecast_ts)))
      .attr('x', (...args) => {
        const d = args[0];
        const i = args[1];
        const selfEle = utilityService.getSelfElement(args);
        const xValue = (i * squareWidth) - (bnd3Service.getSelectionWidth(d3.select(selfEle))
          / 2);
        if (dateFormat(d.forecast_ts) === '00' || dateFormat(d.forecast_ts) === '00Z') {
          d.xValue = xValue + (bnd3Service.getSelectionWidth(d3.select(selfEle)) / 2);
          zeroZuluLineData.push(d);
        }
        return roundOfNumber(xValue);
      })
      .attr('y', isTopAxis
        ? normalTextHeight
        : 0);
    if (isTopAxis) {
      const zeroLines = zeroLineContainer.selectAll('majorDaysLines')
        .data(zeroZuluLineData)
        .enter();
      zeroLines.append('line')
        .attr('class', 'zeroLine')
        .attr('x1', (d) => d.xValue)
        .attr('y1', -normalTextHeight)
        .attr('x2', (d) => d.xValue)
        .attr('y2', majorContainerHeight);
    }
    // const t1 = performance.now();
    // console.log(`Call to doSomething took ${t1 - t0} milliseconds.`);
  };
  return content;
}
export default xAxisGridService();
